import React, { useState } from 'react';
import { formatMoney, formatNumber, formatDate, plural } from '../../../utils/formatters';

const RU_MONTHS_SHORT = ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];
const RU_MONTHS_FULL = ['январь', 'февраль', 'март', 'апрель', 'май', 'июнь', 'июль', 'август', 'сентябрь',
  'октябрь', 'ноябрь', 'декабрь'];
const RU_WEEKDAYS_SHORT = ['вс', 'пн', 'вт', 'ср', 'чт', 'пт', 'сб'];

const monthShort = (m) => RU_MONTHS_SHORT[parseInt(m.slice(5, 7), 10) - 1] || m;
const monthFull = (m) => `${RU_MONTHS_FULL[parseInt(m.slice(5, 7), 10) - 1] || m} ${m.slice(0, 4)}`;
const parseDay = (d) => new Date(`${d.slice(0, 10)}T00:00:00`);
const daysBetween = (a, b) => Math.round((parseDay(b) - parseDay(a)) / 86400000);
const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const daysWord = (n) => `${n} ${plural(n, 'день', 'дня', 'дней')}`;
const visitsWord = (n) => `${formatNumber(n)} ${plural(n, 'визит', 'визита', 'визитов')}`;

/**
 * Одна фраза наверху: когда был и не пора ли ему прийти. Это главный вопрос
 * менеджера к карточке, поэтому ответ — словами, а не набором плиток.
 */
export const Insight = ({ profile, lastVisit, status }) => {
  if (!lastVisit) return null;
  const since = daysBetween(lastVisit, todayStr());
  const lastText = since <= 0 ? 'сегодня' : since === 1 ? 'вчера' : `${daysWord(since)} назад`;
  let rhythm = null;
  if (profile?.typical_gap && profile?.expected_next) {
    const late = daysBetween(profile.expected_next, todayStr());
    const every = profile.typical_gap === 1 ? 'почти каждый день' : `раз в ${daysWord(profile.typical_gap)}`;
    rhythm = late > 0
      ? <>Обычно приходит {every} — <b>опаздывает на {daysWord(late)}</b> (ждали {formatDate(profile.expected_next)}).</>
      : <>Обычно приходит {every} — следующий визит ориентировочно <b>{formatDate(profile.expected_next)}</b>.</>;
  }
  return (
    <div className={`gp-insight st-${status || 'unknown'}`}>
      <p>
        Последний визит — <b>{lastText}</b> ({formatDate(lastVisit)}).{' '}
        {rhythm}
      </p>
    </div>
  );
};

/** Главные числа: ценность, частота, чек, бонусы. */
export const KpiGrid = ({ tiles }) => (
  <div className="gp-kpis">
    {tiles.map((t) => (
      <div key={t.key} className={`gp-kpi ${t.accent ? 'is-accent' : ''}`}>
        <span className="gp-kpi-label">{t.label}</span>
        <strong className="gp-kpi-value">{t.value}</strong>
        {t.sub && <span className="gp-kpi-sub">{t.sub}</span>}
      </div>
    ))}
  </div>
);

/** Траты за неделю / месяц / год / всё время из iiko.biz — одна строка. */
export const PeriodStrip = ({ items }) => (
  <div className="gp-periods">
    {items.map((it) => (
      <div key={it.label}>
        <span>{it.label}</span>
        <b>{formatMoney(Math.round(it.value || 0))}</b>
      </div>
    ))}
  </div>
);

/**
 * Активность по месяцам: столбец — траты, подпись — визиты. Пустые месяцы
 * оставлены намеренно: провал посередине и есть то, что надо увидеть.
 * Касание/наведение показывает месяц в строке над графиком (на телефоне
 * наведения нет, поэтому столбцы — кнопки).
 */
export const ActivityChart = ({ profile }) => {
  const months = profile.monthly;
  const [active, setActive] = useState(null);
  const max = Math.max(1, ...months.map((m) => m.amount));
  const shown = active !== null ? months[active] : null;
  const l90 = profile.last90;
  const p90 = profile.prev90;
  const trend = p90.visits === 0 ? null : Math.round(((l90.visits - p90.visits) / p90.visits) * 100);

  return (
    <section className="gp-card">
      <div className="gp-card-head">
        <h4>Активность</h4>
        <span className="gp-card-note">последние {months.length} мес.</span>
      </div>
      <div className="gp-chart-readout" aria-live="polite">
        {shown ? (
          <>
            <span className="gp-chart-readout-name">{monthFull(shown.month)}</span>
            <span>{shown.visits ? visitsWord(shown.visits) : 'не приходил'}</span>
            {shown.amount > 0 && <b>{formatMoney(shown.amount)}</b>}
          </>
        ) : (
          <>
            <span className="gp-chart-readout-name">За 90 дней</span>
            <span>{visitsWord(l90.visits)}</span>
            <b>{formatMoney(l90.amount)}</b>
            {trend !== null && (
              <span className={`gp-trend ${trend > 0 ? 'up' : trend < 0 ? 'down' : ''}`}>
                {trend > 0 ? '▲' : trend < 0 ? '▼' : '='} {Math.abs(trend)}% к прошлым 90
              </span>
            )}
            {trend === null && l90.visits > 0 && <span className="gp-dim">за 90 дней до этого визитов не было</span>}
          </>
        )}
      </div>
      <div className="gp-chart" onMouseLeave={() => setActive(null)}>
        {months.map((m, i) => (
          <button
            type="button"
            key={m.month}
            className={`gp-chart-col ${active === i ? 'is-active' : ''} ${m.visits ? '' : 'is-empty'}`}
            onMouseEnter={() => setActive(i)}
            onFocus={() => setActive(i)}
            onBlur={() => setActive(null)}
            onClick={() => setActive(active === i ? null : i)}
            aria-label={`${monthFull(m.month)}: ${m.visits ? visitsWord(m.visits) : 'нет визитов'}, ${formatMoney(m.amount)}`}
          >
            <span className="gp-chart-count">{m.visits || ''}</span>
            <span className="gp-chart-bar-wrap">
              <span className="gp-chart-bar" style={{ height: `${Math.max(m.amount ? 6 : 0, (m.amount / max) * 100)}%` }} />
            </span>
            <span className="gp-chart-month">{monthShort(m.month)}</span>
          </button>
        ))}
      </div>
    </section>
  );
};

/** Привычки: день недели, ритм, заказов за визит, стаж. Только то, что есть. */
export const Habits = ({ profile, loyaltySince, firstVisit }) => {
  const items = [];
  if (profile?.weekday) {
    items.push({ k: 'wd', label: 'Любимый день', value: profile.weekday.name, sub: `${Math.round(profile.weekday.share * 100)}% визитов` });
  }
  if (profile?.typical_gap) {
    items.push({ k: 'gap', label: 'Ритм', value: `раз в ${daysWord(profile.typical_gap)}`, sub: 'обычный интервал' });
  }
  if (profile?.orders_per_visit) {
    items.push({ k: 'opv', label: 'Заказов за визит', value: String(profile.orders_per_visit).replace('.', ','), sub: 'в среднем' });
  }
  if (firstVisit) {
    items.push({ k: 'first', label: 'Гость с', value: formatDate(firstVisit), sub: 'первый заказ' });
  }
  if (loyaltySince && !firstVisit) {
    items.push({ k: 'since', label: 'В программе с', value: formatDate(loyaltySince) });
  }
  if (!items.length) return null;
  return (
    <section className="gp-card">
      <div className="gp-card-head"><h4>Привычки</h4></div>
      <div className="gp-habits">
        {items.map((it) => (
          <div key={it.k} className="gp-habit">
            <span className="gp-habit-label">{it.label}</span>
            <b>{it.value}</b>
            {it.sub && <span className="gp-habit-sub">{it.sub}</span>}
          </div>
        ))}
      </div>
    </section>
  );
};

/** Последние визиты по дням — из журнала (один визит = все заказы дня). */
export const RecentVisits = ({ recent }) => {
  const [expanded, setExpanded] = useState(false);
  if (!recent?.length) return null;
  const shown = expanded ? recent : recent.slice(0, 4);
  return (
    <section className="gp-card">
      <div className="gp-card-head"><h4>Последние визиты</h4></div>
      <ul className="gp-visits">
        {shown.map((v) => {
          const d = parseDay(v.day);
          return (
            <li key={v.day}>
              <span className="gp-visit-date">
                <b>{d.getDate()}</b>
                <span>{RU_MONTHS_SHORT[d.getMonth()]}</span>
              </span>
              <span className="gp-visit-main">
                <span>{RU_WEEKDAYS_SHORT[d.getDay()]}, {d.getFullYear()}</span>
                <span className="gp-dim">{v.orders} {plural(v.orders, 'заказ', 'заказа', 'заказов')}</span>
              </span>
              <b className="gp-visit-amount">{formatMoney(v.amount)}</b>
            </li>
          );
        })}
      </ul>
      {recent.length > 4 && (
        <button type="button" className="gp-link" onClick={() => setExpanded(!expanded)}>
          {expanded ? 'Свернуть' : `Ещё ${recent.length - 4}`}
        </button>
      )}
    </section>
  );
};

/** Помесячная динамика для гостей без журнала (статистика по карте). */
export const LegacyTrend = ({ trend }) => {
  const months = (trend || []).slice(-6);
  if (!months.length) return null;
  const max = Math.max(1, ...months.map((m) => m.spend || 0));
  return (
    <section className="gp-card">
      <div className="gp-card-head"><h4>Динамика по месяцам</h4></div>
      <div className="gp-legacy-trend">
        {months.map((m) => (
          <div className="gp-legacy-row" key={m.month}>
            <span className="gp-dim">{monthShort(m.month)} {m.month.slice(0, 4)}</span>
            <span className="gp-legacy-track"><span style={{ width: `${((m.spend || 0) / max) * 100}%` }} /></span>
            <b>{formatMoney(m.spend || 0)}</b>
          </div>
        ))}
      </div>
    </section>
  );
};
